import { boxHitsOtherDisk } from '../../../runNeuralAnimationFrame/labelSlots';
import { overviewViewInsets } from '../../../../utils/neuralSceneUtils';
import {
  clearTitleSeatWant,
  clusterCentroid,
  outerClusterSet,
  taggedBunches,
  titleBoxHitsNames,
} from '../tagLayout';
import { collectNodeScreens, collectVisibleClusterKeys, collectWireScreens } from './collect';
import { hugLiveNodes } from './geometry';
import { huntOneTitle } from './huntTitle';
import { tagPixelBox } from './project';
import { clearViewHold, keepTitleInView, pinTitleLocal, setTitleShown } from './titleView';
import { ndc, nodeScreens, tagWorld, VIEW_PAD_PX, wireScreens } from './vectors';

export { projectNebulaTitleBoxes } from './project';

/** Slide each cloud tag around its rim. Outer islands use the open outside. */
export function placeNebulaTags(group, camera, meshByTaskId, container, edgeLinesByKey, occupiedBoxes, idleOrbit = false) {
  const cw = Math.max(container?.clientWidth || 1, 1);
  const ch = Math.max(container?.clientHeight || 1, 1);
  const viewPad = (VIEW_PAD_PX / Math.min(cw, ch)) * 2;
  const insets = overviewViewInsets(container);
  const topInset = 2 * insets.top;
  const leftInset = 2 * insets.left;
  const names = occupiedBoxes || [];
  const liveKeys = collectVisibleClusterKeys(camera, meshByTaskId);
  const padX = (14 / cw) * 2;
  const padY = (14 / ch) * 2;
  const nodePad = Math.max(padX, padY);
  collectNodeScreens(camera, meshByTaskId, nodeScreens);
  nodeScreens.forEach((node) => { node.pad = nodePad; });
  const nodePixels = nodeScreens.map((n) => ({
    x: (n.x * 0.5 + 0.5) * cw,
    y: (-n.y * 0.5 + 0.5) * ch,
    r: n.r * 0.5 * cw,
  }));
  const titleOnDisk = (box) => boxHitsOtherDisk(box, nodePixels, null, 8);
  if (idleOrbit) {
    let needsHunt = false;
    const titleBoxes = new Map();
    taggedBunches(group).forEach((bunch) => {
      const tag = bunch.userData.tag;
      if (!tag?.element || tag.element.dataset.editing === '1') return;
      if (!liveKeys.has(bunch.userData.clusterKey)) {
        setTitleShown(tag, false);
        clearViewHold(bunch);
        bunch.userData.tagViewPull = false;
        return;
      }
      setTitleShown(tag, true);
      hugLiveNodes(bunch, meshByTaskId);
      if (!pinTitleLocal(bunch, tag)) needsHunt = true;
      const lwPx = tag.element.offsetWidth || 80;
      const lhPx = tag.element.offsetHeight || 16;
      keepTitleInView(camera, bunch, tag, lwPx, lhPx, cw, ch, viewPad, titleBoxes, topInset, 0);
      const seated = titleBoxes.get(bunch);
      if (titleBoxHitsNames(seated, names) || titleOnDisk(seated)) {
        needsHunt = true;
        bunch.userData.titleSeatLock = 0;
        clearTitleSeatWant(bunch.userData);
      }
    });
    if (!needsHunt) return;
  }
  collectWireScreens(camera, edgeLinesByKey, wireScreens);
  const wirePad = Math.max((12 / cw) * 2, (12 / ch) * 2);
  const bunches = taggedBunches(group);
  const mid = clusterCentroid(bunches);
  const outer = outerClusterSet(bunches);
  const titleBoxes = new Map();
  bunches.forEach((bunch) => {
    const tag = bunch.userData.tag;
    if (!tag?.element || tag.element.dataset.editing === '1') return;
    if (!liveKeys.has(bunch.userData.clusterKey)) return;
    tag.getWorldPosition(tagWorld);
    ndc.copy(tagWorld).project(camera);
    if (ndc.z < -1 || ndc.z > 1) return;
    const lwPx = tag.element.offsetWidth || 80;
    const lhPx = tag.element.offsetHeight || 16;
    titleBoxes.set(bunch, tagPixelBox(ndc, lwPx, lhPx, cw, ch));
  });
  const ordered = bunches.slice().sort((a, b) => {
    const ao = outer.has(a) ? 1 : 0;
    const bo = outer.has(b) ? 1 : 0;
    if (ao !== bo) return bo - ao;
    return (b.userData.halfW || 0) - (a.userData.halfW || 0);
  });
  const huntCtx = {
    camera, group, bunches, mid, outer, names, titleBoxes,
    nodeScreens, nodePixels, wireScreens, wirePad,
    cw, ch, padX, padY, viewPad, topInset, leftInset,
  };
  ordered.forEach((bunch) => {
    const tag = bunch.userData.tag;
    if (!tag || tag.element.dataset.editing === '1') return;
    if (!liveKeys.has(bunch.userData.clusterKey)) {
      setTitleShown(tag, false);
      clearViewHold(bunch);
      return;
    }
    if (idleOrbit && bunch.userData.tagLocalX != null) {
      const seated = titleBoxes.get(bunch);
      if (seated && !titleBoxHitsNames(seated, names) && !titleOnDisk(seated)) return;
      bunch.userData.titleSeatLock = 0;
    }
    setTitleShown(tag, true);
    hugLiveNodes(bunch, meshByTaskId);
    huntOneTitle(bunch, huntCtx);
  });
}
